import upperFirst from 'lodash/upperFirst';
import { useCallback, useContext, useMemo } from 'react';
import { ProfileSettingsContext } from './ProfileSettingsContext';
import {
  ProfileSettingName,
  ProfileSettingsState,
  SettingTypes,
  UpdateFunction,
} from './types';

export function useUserProfileSetting<SettingKey extends ProfileSettingName>(
  settingName: SettingKey,
) {
  type Value = SettingTypes[SettingKey];
  type ValueSetter = (updateFunction: UpdateFunction<SettingKey>) => void;
  type SetterKey = `set${Capitalize<SettingKey>}`;

  type Settings = {
    [key in 'setting' | SettingKey]: Value | undefined;
  };
  type Setters = {
    [key in 'setSetting' | SetterKey]: ValueSetter;
  };
  type SettingProgress = Pick<ProfileSettingsState, 'error' | 'isSaving'>;

  const { settings, setSetting, error, isSaving } = useContext(
    ProfileSettingsContext,
  );

  const setThisSetting = useCallback<ValueSetter>(
    (updateFunction) => setSetting(settingName, updateFunction),
    [setSetting, settingName],
  );

  return useMemo(
    () =>
      ({
        setting: settings[settingName],
        setSetting: setThisSetting,
        [settingName as SettingKey]: settings[settingName],
        [`set${upperFirst(settingName)}` as SetterKey]: setThisSetting,
        error,
        isSaving,
      }) as Readonly<Settings & Setters & SettingProgress>,
    [setThisSetting, settingName, settings, error, isSaving],
  );
}
